import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { authGuardFn } from '@core/authentication/auth.guard';
import { constructionDrawsEnabledGuardFn } from '@core/guards/construction-draws-enabled/construction-draws-enabled.guard';
import { emailAuthenticatedGuardFn } from '@core/guards/email-authenticated/email-authenticated.guard';
import { reportsEnabledGuardFn } from '@core/guards/reports-enabled/reports-enabled.guard';
import { scorecardsEnabledGuardFn } from '@core/guards/scorecards-enabled/scorecards-enabled.guard';
import { OIQ_WORKFLOW_APPLICATION_STORE_PROVIDER_FACTORY } from '@v2shared/components/workflow/application/application.store';
import {
  OIQ_WORKFLOW_CONFIG_PROVIDER,
  OIQ_WORKFLOW_DECISION_CONFIG_PROVIDER,
} from '@v2shared/components/workflow/application/application.workflow';
import {
  WorkflowAccessGuard,
  workflowAccessGuardFn,
  workflowAccessResolverFn,
} from '@v2shared/components/workflow/workflow-access.guard';
import { OIQ_WORKFLOW_STORE_PROVIDER_FACTORY } from '@v2shared/components/workflow/workflow.store';
import { isoPortalResolverFn } from './iso-portal/resolver/iso-portal.resolver';
import { homeAuthenticatedGuard } from './modules/home/guards/home-authenticated.guard';
import { syndicatorHomeRedirectGuard } from './modules/home/guards/syndicator-home-redirect.guard';
import { tenantResolver } from './modules/queue/resolvers/tenant.resolver';
import {
  V2_REPORTS_ACCESS_RESOURCE,
  V2_REPORTS_ACCESS_ROLES,
  V2RoleGuard,
} from './v2-routing.guards';

const routes: Routes = [
  // Special standalone module with its own layout
  {
    path: '',
    children: [
      {
        path: 'iso-portal',
        loadChildren: () => import('./iso-portal').then(m => m.IsoPortalModule),
        resolve: {
          isoPortalData: isoPortalResolverFn,
        },
      },
      {
        path: 'home',
        canActivate: [homeAuthenticatedGuard, syndicatorHomeRedirectGuard],
        loadComponent: () =>
          import('./modules/home/pages/home/home.component').then(m => m.HomeComponent),
        loadChildren: () => import('./modules/home/home.routes').then(m => m.homeRoutes),
        resolve: {
          tenant: tenantResolver,
        },
      },
      {
        path: '',
        canActivate: [authGuardFn],
        children: [
          { path: '', redirectTo: 'applications', pathMatch: 'full' },
          {
            path: 'applications',
            loadChildren: () =>
              import('./modules/applications/applications.module').then(m => m.ApplicationsModule),
          },
          {
            path: 'isos',
            loadChildren: () => import('./modules/isos/isos.module').then(m => m.IsosModule),
          },
          {
            path: 'clients',
            loadChildren: () =>
              import('./modules/clients/clients.module').then(m => m.ClientsModule),
          },
          {
            path: 'law-firms',
            loadChildren: () =>
              import('./modules/law-firms/law-firms.module').then(m => m.LawFirmsModule),
          },
          {
            path: 'inspection-companies',
            canActivate: [constructionDrawsEnabledGuardFn],
            loadChildren: () =>
              import('./modules/inspection-companies/inspection-companies.module').then(
                m => m.InspectionCompaniesModule
              ),
          },
          {
            path: 'syndicators',
            loadChildren: () =>
              import('./modules/syndicators/syndicators.module').then(m => m.SyndicatorsModule),
          },
          {
            path: 'scorecard',
            canActivate: [scorecardsEnabledGuardFn],
            loadChildren: () =>
              import('./modules/scorecard/scorecard.module').then(m => m.V2ScorecardModule),
          },
          {
            path: 'reports',
            canActivate: [
              reportsEnabledGuardFn,
              V2RoleGuard([...V2_REPORTS_ACCESS_ROLES], V2_REPORTS_ACCESS_RESOURCE),
            ],
            loadChildren: () =>
              import('./modules/reports/reports.module').then(m => m.ReportsModule),
          },
          {
            path: 'workflow',
            loadChildren: () =>
              import('./shared/components/workflow/workflow.module').then(m => m.OiqWorkflowModule),
            canActivate: [workflowAccessGuardFn],
            resolve: {
              workflowAccess: workflowAccessResolverFn,
            },
            providers: [
              WorkflowAccessGuard,
              OIQ_WORKFLOW_CONFIG_PROVIDER,
              OIQ_WORKFLOW_DECISION_CONFIG_PROVIDER,
              OIQ_WORKFLOW_APPLICATION_STORE_PROVIDER_FACTORY,
              OIQ_WORKFLOW_STORE_PROVIDER_FACTORY,
            ],
          },
        ],
      },
      {
        path: 'email-app',
        canActivate: [emailAuthenticatedGuardFn],
        loadChildren: () =>
          import('./modules/email-app/email-app.module').then(m => m.EmailAppModule),
      },
      {
        path: 'error',
        loadChildren: () => import('./error').then(m => m.ErrorModule),
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class V2RoutingModule {}
